import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { 
  Palette, 
  Upload, 
  Globe, 
  Users, 
  Settings, 
  Eye, 
  Edit, 
  Trash2,
  Plus,
  Download,
  ExternalLink,
  Crown,
  Building2,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { 
  ConfigurationForm, 
  PartnerForm, 
  TenantForm, 
  AnalyticsDashboard 
} from "./WhiteLabelForms";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhiteLabelManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [tenants, setTenants] = useState([]);
  
  // Dialog states
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  
  // Form states
  const [configForm, setConfigForm] = useState({
    tenant_id: '',
    company_name: '',
    logo_url: '',
    favicon_url: '',
    primary_color: '#1f2937',
    secondary_color: '#3b82f6',
    accent_color: '#10b981',
    font_family: 'Inter',
    custom_domain: '',
    subdomain: '',
    custom_css: '',
    hide_footer_branding: false,
    custom_login_background: ''
  });
  
  const [partnerForm, setPartnerForm] = useState({
    partner_name: '',
    contact_email: '',
    contact_phone: '',
    company_address: '',
    revenue_share_percentage: 0,
    commission_rate: 0,
    max_clients: -1,
    allowed_features: [],
    default_tier: 'starter'
  });
  
  const [tenantForm, setTenantForm] = useState({
    partner_id: '',
    tenant_name: '',
    contact_email: '',
    subscription_tier: 'starter'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configsRes, partnersRes, tenantsRes] = await Promise.all([
        axios.get(`${API}/whitelabel/configs`).catch(() => ({ data: [] })),
        axios.get(`${API}/partners`).catch(() => ({ data: [] })),
        axios.get(`${API}/tenants`).catch(() => ({ data: [] }))
      ]);
      
      setConfigs(configsRes.data);
      setPartners(partnersRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      console.error("Error fetching white-label data:", error);
      toast.error("Failed to load white-label data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, assetType) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_type', assetType);
      
      const response = await axios.post(`${API}/whitelabel/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`${assetType} uploaded successfully`);
      return response.data.url;
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(`Failed to upload ${assetType}`);
      return null;
    }
  };

  const createConfig = async () => {
    try {
      await axios.post(`${API}/whitelabel/config`, configForm);
      toast.success("White-label configuration created successfully");
      setShowConfigDialog(false);
      resetConfigForm();
      fetchData();
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : "Failed to create configuration";
      toast.error(errorMessage);
    }
  };

  const updateConfig = async () => {
    try {
      await axios.put(`${API}/whitelabel/config/${editingConfig.id}`, configForm);
      toast.success("Configuration updated successfully");
      setShowConfigDialog(false);
      resetConfigForm();
      setEditingConfig(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update configuration");
    }
  };

  const createPartner = async () => {
    try {
      await axios.post(`${API}/partners`, partnerForm);
      toast.success("Partner created successfully");
      setShowPartnerDialog(false);
      resetPartnerForm();
      fetchData();
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : "Failed to create partner";
      toast.error(errorMessage);
    }
  };

  const createTenant = async () => {
    try {
      await axios.post(`${API}/tenants`, tenantForm);
      toast.success("Tenant created successfully");
      setShowTenantDialog(false);
      resetTenantForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to create tenant");
    }
  };

  const resetConfigForm = () => {
    setConfigForm({
      tenant_id: '',
      company_name: '',
      logo_url: '',
      favicon_url: '',
      primary_color: '#1f2937',
      secondary_color: '#3b82f6',
      accent_color: '#10b981',
      font_family: 'Inter',
      custom_domain: '',
      subdomain: '',
      custom_css: '',
      hide_footer_branding: false,
      custom_login_background: ''
    });
  };

  const resetPartnerForm = () => {
    setPartnerForm({
      partner_name: '',
      contact_email: '',
      contact_phone: '',
      company_address: '',
      revenue_share_percentage: 0,
      commission_rate: 0,
      max_clients: -1,
      allowed_features: [],
      default_tier: 'starter'
    });
  };

  const resetTenantForm = () => {
    setTenantForm({
      partner_id: '',
      tenant_name: '',
      contact_email: '',
      subscription_tier: 'starter'
    });
  };

  const openEditConfig = (config) => {
    setEditingConfig(config);
    setConfigForm({ ...config });
    setShowConfigDialog(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">White-Label Management</h1>
          <p className="text-gray-600">Manage white-label configurations, partners, and tenants</p>
        </div>
        <Badge className="bg-purple-100 text-purple-700">
          <Crown className="h-4 w-4 mr-1" />
          Enterprise Feature
        </Badge>
      </div>

      <Tabs defaultValue="configurations" className="space-y-4">
        <TabsList>
          <TabsTrigger value="configurations">Configurations</TabsTrigger>
          <TabsTrigger value="partners">Partners</TabsTrigger>
          <TabsTrigger value="tenants">Tenants</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* White-Label Configurations Tab */}
        <TabsContent value="configurations" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">White-Label Configurations</h2>
            <Button 
              onClick={() => setShowConfigDialog(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Configuration
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {configs.map((config) => (
              <ConfigCard 
                key={config.id} 
                config={config} 
                onEdit={openEditConfig}
                onRefresh={fetchData}
              />
            ))}
            
            {configs.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                <Palette className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No white-label configurations found</p>
                <Button 
                  className="mt-4" 
                  onClick={() => setShowConfigDialog(true)}
                >
                  Create First Configuration
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Partners Tab */}
        <TabsContent value="partners" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Partners & Resellers</h2>
            <Button 
              onClick={() => setShowPartnerDialog(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Partner
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {partners.map((partner) => (
              <PartnerCard key={partner.id} partner={partner} onRefresh={fetchData} />
            ))}
          </div>
        </TabsContent>

        {/* Tenants Tab */}
        <TabsContent value="tenants" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Tenants</h2>
            <Button 
              onClick={() => setShowTenantDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Tenant
            </Button>
          </div>

          <div className="space-y-4">
            {tenants.map((tenant) => (
              <TenantCard key={tenant.id} tenant={tenant} partners={partners} />
            ))}
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <AnalyticsDashboard 
            configs={configs}
            partners={partners} 
            tenants={tenants}
          />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingConfig ? 'Edit' : 'Create'} White-Label Configuration
            </DialogTitle>
          </DialogHeader>
          
          <ConfigurationForm 
            form={configForm}
            setForm={setConfigForm}
            onSubmit={editingConfig ? updateConfig : createConfig}
            onCancel={() => {
              setShowConfigDialog(false);
              resetConfigForm();
              setEditingConfig(null);
            }}
            onFileUpload={handleFileUpload}
            isEditing={!!editingConfig}
          />
        </DialogContent>
      </Dialog>

      {/* Create Partner Dialog */}
      <Dialog open={showPartnerDialog} onOpenChange={setShowPartnerDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Partner</DialogTitle>
          </DialogHeader>
          
          <PartnerForm 
            form={partnerForm}
            setForm={setPartnerForm}
            onSubmit={createPartner}
            onCancel={() => {
              setShowPartnerDialog(false);
              resetPartnerForm();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Create Tenant Dialog */}
      <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Add New Tenant</DialogTitle>
          </DialogHeader>
          
          <TenantForm 
            form={tenantForm}
            setForm={setTenantForm}
            partners={partners}
            onSubmit={createTenant}
            onCancel={() => {
              setShowTenantDialog(false);
              resetTenantForm();
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Configuration Card Component
const ConfigCard = ({ config, onEdit, onRefresh }) => (
  <Card className="hover:shadow-md transition-shadow">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div 
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: config.primary_color }}
          />
          <CardTitle className="text-base">{config.company_name}</CardTitle>
        </div>
        <div className="flex space-x-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(config)}
          >
            <Edit className="h-3 w-3" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`https://${config.subdomain}.example.com`, '_blank')}
            disabled={!config.subdomain}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <CardDescription>{config.subdomain || 'No subdomain'}</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Domain</span>
          <span className="font-medium">
            {config.custom_domain || config.subdomain + '.example.com' || 'Not set'}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Font</span>
          <span className="font-medium">{config.font_family}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Status</span>
          <Badge variant={config.is_active ? "default" : "secondary"}>
            {config.is_active ? "Active" : "Inactive"}
          </Badge>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Partner Card Component
const PartnerCard = ({ partner, onRefresh }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-lg">{partner.partner_name}</CardTitle>
        <Badge className="bg-green-100 text-green-700">
          <Building2 className="h-3 w-3 mr-1" />
          Partner
        </Badge>
      </div>
      <CardDescription>{partner.contact_email}</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-600">Revenue Share</span>
          <p className="font-medium">{partner.revenue_share_percentage}%</p>
        </div>
        <div>
          <span className="text-gray-600">Commission</span>
          <p className="font-medium">{partner.commission_rate}%</p>
        </div>
        <div>
          <span className="text-gray-600">Max Clients</span>
          <p className="font-medium">
            {partner.max_clients === -1 ? 'Unlimited' : partner.max_clients}
          </p>
        </div>
        <div>
          <span className="text-gray-600">Default Tier</span>
          <p className="font-medium capitalize">{partner.default_tier}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Tenant Card Component
const TenantCard = ({ tenant, partners }) => {
  const partner = partners.find(p => p.id === tenant.partner_id);
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{tenant.tenant_name}</CardTitle>
            <CardDescription>{tenant.contact_email}</CardDescription>
          </div>
          <div className="text-right">
            <Badge className="capitalize">
              {tenant.subscription_tier}
            </Badge>
            {partner && (
              <p className="text-xs text-gray-500 mt-1">
                via {partner.partner_name}
              </p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Revenue</span>
            <p className="font-medium">${tenant.monthly_revenue}</p>
          </div>
          <div>
            <span className="text-gray-600">Users</span>
            <p className="font-medium">{tenant.total_users}</p>
          </div>
          <div>
            <span className="text-gray-600">Plans</span>
            <p className="font-medium">{tenant.total_plans}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WhiteLabelManager;